import { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import type { AgendaRoom } from "@domaddress/shared";
import type { CalendarItem, GoogleCalendarMeta } from "../lib/calendarTypes";
import { getVisibleRange } from "../lib/calendarDate";
import { mergeMatchedCalendarItems } from "../lib/calendarMatch";
import AgendaCalendar from "../components/AgendaCalendar";

type ViewMode = "month" | "week" | "list";

export default function CalendarPage() {
  const [rooms, setRooms] = useState<AgendaRoom[]>([]);
  const [items, setItems] = useState<CalendarItem[]>([]);
  const [googleCalendars, setGoogleCalendars] = useState<GoogleCalendarMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentDate, setCurrentDate] = useState(() => new Date());
  const [viewMode, setViewMode] = useState<ViewMode>("month");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { from, to } = getVisibleRange(currentDate, viewMode);
      const [roomsRes, itemsRes, googleRes] = await Promise.all([
        api.get<{ data: AgendaRoom[] }>("/api/rooms"),
        api.get<{ data: CalendarItem[] }>(`/api/calendar/items?from=${from}&to=${to}`),
        api.get<{ data: CalendarItem[]; calendars: GoogleCalendarMeta[] }>(
          `/api/calendar/google-items?from=${from}&to=${to}`
        ),
      ]);
      setRooms(roomsRes.data.data);
      const merged = mergeMatchedCalendarItems([
        ...itemsRes.data.data,
        ...googleRes.data.data,
      ]);
      setItems(merged);
      setGoogleCalendars(googleRes.data.calendars);
    } catch {
      setError("Kon de agenda niet laden. Probeer het opnieuw.");
    } finally {
      setLoading(false);
    }
  }, [currentDate, viewMode]);

  useEffect(() => {
    void load();
  }, [load]);

  const allItems = items;

  return (
    <div className="page calendar-page">
      <header className="page-header">
        <h1>Kalender</h1>
        <p className="page-subtitle">
          Evenementen (met zalen), en Google Agenda&apos;s. Gekoppelde items (paars met
          koppel-icoon) combineren een evenement met het zaalrooster.
        </p>
      </header>

      {error && <p className="form-error">{error}</p>}

      {!loading && !error && allItems.length === 0 && (
        <p className="page-subtitle dom-calendar-hint">
          Geen items in deze periode. Gebruik de pijltjes om een andere maand of week te bekijken.
        </p>
      )}

      <AgendaCalendar
        items={allItems}
        rooms={rooms}
        googleCalendars={googleCalendars}
        loading={loading}
        currentDate={currentDate}
        onCurrentDateChange={setCurrentDate}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
      />
    </div>
  );
}
